import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, CaretLeft, CaretRight, Target, Lightbulb } from '@phosphor-icons/react';

import type { CoachCard, CoachGoalView, CoachStarterCard } from '@/api/coach';
import { CoachCardRow, StarterCardRow, askCoach } from '@/components/coach/cards';

// ActionCenter — the "to do" column. Two parts:
//  1. The assistant's ranked suggestions, shown ONE at a time (most important
//     first) with a position indicator and prev/next, so the list never
//     overwhelms — the header badge still shows the true total, and a link opens
//     the full assistant.
//  2. "Improve your goals" — a concrete action per chosen goal so there's always
//     something forward-looking to do: a goal with a setup step links there, and
//     any other goal opens the AI coach for tailored, club-specific ideas.
export function ActionCenter({
  slug,
  cards,
  starters,
  goals,
  goalTitle,
}: {
  slug: string;
  cards: CoachCard[];
  starters: CoachStarterCard[];
  goals: CoachGoalView[];
  goalTitle: (key: string) => string;
}) {
  const { t } = useTranslation('common');
  const [index, setIndex] = useState(0);

  const current = cards.length > 0 ? Math.min(index, cards.length - 1) : 0;
  const card = cards[current];

  const starterByGoal = new Map(starters.map((s) => [s.goalKey, s]));

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-heading text-lg font-bold tracking-tight">
          {t('home.actionCenter.title')}
        </h2>
        {cards.length > 0 && (
          <span className="rounded-pill bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground">
            {cards.length}
          </span>
        )}
      </div>

      {card ? (
        <div className="flex flex-col gap-2">
          <CoachCardRow
            key={card.suggestionId}
            card={card}
            slug={slug}
            goalTitle={goalTitle}
            risk={card.tone === 'destructive'}
          />
          {/* One-at-a-time controls: position + step through, plus the escape
              hatch to the full assistant. Only shown when there's more than one. */}
          <div className="flex items-center justify-between gap-2">
            {cards.length > 1 ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label={t('home.actionCenter.prev')}
                  disabled={current === 0}
                  onClick={() => setIndex(current - 1)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition hover:text-foreground disabled:opacity-30"
                >
                  <CaretLeft size={16} weight="bold" aria-hidden />
                </button>
                <span className="min-w-14 text-center text-xs font-semibold tabular-nums text-muted-foreground">
                  {current + 1} / {cards.length}
                </span>
                <button
                  type="button"
                  aria-label={t('home.actionCenter.next')}
                  disabled={current >= cards.length - 1}
                  onClick={() => setIndex(current + 1)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition hover:text-foreground disabled:opacity-30"
                >
                  <CaretRight size={16} weight="bold" aria-hidden />
                </button>
              </div>
            ) : (
              <span />
            )}
            <Link
              to={`/orgs/${slug}/assistant`}
              className="inline-flex items-center gap-1 rounded-pill px-2 py-1 text-xs font-semibold text-muted-foreground transition hover:text-foreground"
            >
              {t('home.actionCenter.viewAll')}
              <ArrowRight size={14} weight="bold" aria-hidden />
            </Link>
          </div>
        </div>
      ) : (
        <p className="flex items-center gap-2 rounded-xl border border-border bg-background p-4 text-sm text-muted-foreground">
          <CheckCircle size={20} weight="bold" aria-hidden />
          {t('coach.dashboard.empty')}
        </p>
      )}

      {/* Improve your goals — always something forward-looking to do, even when
          the assistant has flagged nothing. */}
      {goals.length > 0 && (
        <div className="mt-2 flex flex-col gap-2">
          <h3 className="flex items-center gap-1.5 font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">
            <Target size={14} weight="bold" aria-hidden />
            {t('home.actionCenter.improveTitle')}
          </h3>
          <ul className="flex flex-col gap-3">
            {goals.map((g) => {
              const starter = starterByGoal.get(g.goalKey);
              if (starter) {
                return (
                  <StarterCardRow key={g.goalKey} starter={starter} slug={slug} goalTitle={goalTitle} />
                );
              }
              return <ImproveGoalRow key={g.goalKey} label={goalTitle(g.goalKey)} />;
            })}
          </ul>
        </div>
      )}
    </section>
  );
}

// ImproveGoalRow — a goal with no concrete setup step still gets an action:
// ask the AI coach for tailored, club-specific ways to move it. Uses the same
// seeded-prompt mechanism as tapping a goal chip.
function ImproveGoalRow({ label }: { label: string }) {
  const { t } = useTranslation('common');
  return (
    <li className="flex flex-col gap-2 rounded-xl border border-border bg-background p-4 md:flex-row md:items-center md:justify-between md:gap-4">
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-pill bg-secondary px-2 py-0.5 text-xs font-semibold text-secondary-foreground">
            {label}
          </span>
          <h4 className="font-heading text-base font-semibold">
            {t('home.actionCenter.improveRowTitle')}
          </h4>
        </div>
        <p className="text-sm text-muted-foreground">{t('home.actionCenter.improveRowHint')}</p>
      </div>
      <button
        type="button"
        onClick={() => askCoach(t('coach.dashboard.howToPrompt', { goal: label }))}
        className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-pill border border-border px-4 py-2 text-sm font-semibold transition hover:scale-[1.04] motion-reduce:hover:scale-100"
      >
        <Lightbulb size={16} weight="bold" aria-hidden />
        {t('home.actionCenter.improveCta')}
      </button>
    </li>
  );
}
